import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta as MetaService } from '@angular/platform-browser';
import { App } from '@df/core/app';
import { NCore } from '@df/core/df-core.interfaces';
import { Client } from '@df/core/service/client';
import { TimeoutService } from '@df/ng/timeout.service';
import { AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import each from 'lodash-es/each';
import { BehaviorSubject } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class SsrService {
    private renderer2!: Renderer2;
    private waitingQty = 0;
    private ready$ = new BehaviorSubject(false);
    private document = inject(DOCUMENT);
    private rendererFactory = inject(RendererFactory2);
    private app = inject(App);
    private afterContentInitDirectiveService = inject(AfterContentInitDirectiveService);
    private client = inject(Client);
    private metaService = inject(MetaService);
    private moduleConfig = inject(NCore.OPTIONS);
    private timeoutService = inject(TimeoutService);
    private destroyRef = inject(DestroyRef);

    constructor() {
        // add subscription to wait for content init at least
        this.wait(
            new Promise<void>(resolve => {
                const subscription = this.afterContentInitDirectiveService.ready.subscribe(value => {
                    if (value) {
                        this.timeoutService.setTimeout(
                            () => {
                                resolve();
                            },
                            ['prerender', 'legacyPrerender'].includes(this.client.mode) ? 3000 : 200
                        );
                        subscription.unsubscribe();
                    }
                });
            }),
            0,
            true
        );
    }

    init() {
        // this interval is to make sure that app is not stable till we finish rendering
        this.renderer2 = this.rendererFactory.createRenderer(null, null);

        if (this.app.isPlatformBrowser) {
            this.renderer2.removeClass(this.document.body, 'cursor-wait');

            const quickRunScript = this.document.getElementById('quickRunScript');

            if (quickRunScript) {
                quickRunScript.remove();
            }

            this.afterContentInitDirectiveService.loaded.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
                if (value) {
                    // remove all links with un-app-link attribute (meta alternate service adds them)
                    each(this.document.querySelectorAll('link[un-app-link]'), i => i.remove());
                }
            });
        } else {
            this.renderer2.addClass(this.document.body, 'cursor-wait');

            if (this.client.sizeId > 1) {
                const sizeClassScript = this.renderer2.createElement('script');
                this.renderer2.setAttribute(sizeClassScript, 'id', 'desktopSizeClassScript');
                sizeClassScript.innerHTML = `
                function desktopSizeClass() {
                    const body = document.body;
                    const w = window.innerWidth;
                    if (w >= ${this.moduleConfig.breakpoints.X.width} && body.classList.contains('size-l')) {
                        body.classList.remove('size-l');
                        body.classList.add('size-x');
                    } else if (w < ${this.moduleConfig.breakpoints.X.width} && body.classList.contains('size-x')) {
                        body.classList.remove('size-x');
                        body.classList.add('size-l');
                    }
                }
                // select link with rel stylesheet that has href that includes styles.css
                const link = this.document.querySelector("link[rel='stylesheet'][href*='styles.css']");
                if (link) {
                    const existingOnload = link.getAttribute('onload') || '';
                    link.setAttribute('onload', existingOnload + ';desktopSizeClass();');
                    link.setAttribute('onerror', existingOnload + ';desktopSizeClass();');
                } else {
                    desktopSizeClass();
                }
                
            `;

                this.document.body.insertBefore(sizeClassScript, this.document.body.firstChild);
            }

            const intId = setInterval(() => {}, 500);
            this.ready$.subscribe(value => {
                if (value) {
                    this.addCacheTagsMap();
                    clearInterval(intId);
                }
            });

            each(this.document.head.querySelectorAll('link'), link => {
                if (link.href && link.href.includes('styles.css?')) {
                    const href = link.getAttribute('href');
                    if (href) {
                        this.renderer2.setAttribute(link, 'unHref', href);
                        this.renderer2.setAttribute(link, 'href', href.replace(/\?.*/, ''));
                    }
                }
            });
        }
    }

    wait(promise: Promise<any>, timeout: number = 0, force: boolean = false) {
        if (this.ready$.value || (!force && ['browser', 'universal'].includes(this.client.mode))) {
            return;
        }

        this.waitingQty++;

        let expired = false;
        let timeoutId: number | undefined;

        const onFinish = () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = undefined;
            }
            this.timeoutService.setTimeout(() => {
                if (!expired) {
                    expired = true;
                    this.waitingQty--;
                    if (this.waitingQty < 1) {
                        this.ready$.next(true);
                    }
                }
            }, 200); // lets wait a bit more
        };
        if (timeout) {
            timeoutId = this.timeoutService.setTimeout(() => {
                onFinish();
            }, timeout);
        }
        promise.finally(onFinish);
    }

    /**
     * Buffer data in script
     */
    private addCacheTagsMap() {
        const ssrTags: Record<string, (number | string)[]> = {};

        each(this.app.resources, resource => {
            let invalidates = false;
            switch (this.client.mode) {
                case 'ssr':
                    invalidates = resource.invalidatesUniversal || resource.invalidatesPrerender;
                    break;
                case 'prerender':
                case 'legacyPrerender':
                    invalidates = resource.invalidatesPrerender;
                    break;
                case 'universal':
                    invalidates = resource.invalidatesUniversal;
                    break;
            }

            if (invalidates) {
                const ids = resource
                    .getAllExisting()
                    .map(i => i.id)
                    .filter(i => (typeof i === 'number' ? i > 0 : !!i));
                if (ids.length) {
                    ssrTags[resource.type] = ids;
                }
            }
        });

        this.metaService.updateTag({ name: 'cache-tags', content: btoa(JSON.stringify(ssrTags)) });
    }
}
